import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProfileContext } from "../../../context/ProfileProvider";
import { alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { resolveUserAvatarUrl } from "../../../api/apiConfig/apiConfig";
import AuthService from "../../../api/services/AuthService";
import { spotMeActivityItemsFromResponse } from "../../../api/services/spotMeActivityQuery";
import { extractFuturesTrades } from "../Order/futuresProfileOrderHelpers";
import { openKycVerification } from "../KycVara/openKycVerification";
import {
  coerceKycVerified,
  isKycVerified as isKycVerifiedTier,
  kycBadgeLabel,
  kycBadgeTone,
  KYC,
} from "../../../utils/kycVerified";

/** Same as asset overview / wallet portfolio — empty API fields render as dash, not 0 */
const formatEstimateSegment = (value, decimals = 5) => {
  if (value === undefined || value === null || value === "") return "—";
  const num = Number(value);
  if (num == 0) return "0.00";
  if (!Number.isFinite(num)) return "—";
  return parseFloat(num.toFixed(decimals));
};

const WALLET_SNAPSHOT_PORTFOLIO_TYPE = "";

const maskEmail = (email) => {
  if (!email || typeof email !== "string") return "--";
  const [localPart, domainPart] = email.split("@");
  if (!localPart || !domainPart) return email;
  if (localPart.length <= 2) return `${localPart[0] || "*"}***@${domainPart}`;
  return `${localPart[0]}***${localPart[localPart.length - 1]}@${domainPart}`;
};

const maskPhone = (phone, countryCode = "") => {
  if (!phone || phone === "--") return "--";
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length < 4) return "--";
  const prefix = countryCode ? `${String(countryCode).trim()} ` : "";
  return `${prefix}****${digits.slice(-4)}`;
};

const DashboardTopContent = ({ userDetails = {} }) => {
  const navigate = useNavigate();
  const { estimatedPortfolio, fetchEstimatedPortfolio, handleUserDetails } = useContext(ProfileContext);

  useEffect(() => {
    if (fetchEstimatedPortfolio) void fetchEstimatedPortfolio(WALLET_SNAPSHOT_PORTFOLIO_TYPE);
  }, [fetchEstimatedPortfolio]);

  useEffect(() => {
    handleUserDetails?.();
  }, [handleUserDetails]);

  const snapshotUsdt = useMemo(() => {
    const ep = estimatedPortfolio || {};
    return (
      ep?.dollarPrice ??
      ep?.estimated_total_usdt ??
      ep?.estimatedTotalUsdt ??
      ep?.estimated_total ??
      ep?.total_usdt
    );
  }, [estimatedPortfolio]);

  const snapshotPreferred = useMemo(() => {
    const ep = estimatedPortfolio || {};
    const pref = ep?.estimated_total_preferred ?? ep?.estimatedTotalPreferred;
    if (pref !== undefined && pref !== null && pref !== "") return pref;
    return snapshotUsdt;
  }, [estimatedPortfolio, snapshotUsdt]);

  const snapshotPreferredCurrency = useMemo(() => {
    const ep = estimatedPortfolio || {};
    return (
      ep?.currency_prefrence ??
      ep?.currency_preference ??
      ep?.preferred_currency ??
      ep?.Currency ??
      "USD"
    );
  }, [estimatedPortfolio]);

  /** Fiat label on the secondary “≈” line (typically USD), same as wallet header */
  const snapshotApproxCurrency = useMemo(() => {
    const ep = estimatedPortfolio || {};
    const c = ep?.Currency || ep?.currency || ep?.fiat_currency;
    return c ? String(c) : "USD";
  }, [estimatedPortfolio]);

  const [walletSnapshotBalanceVisible, setWalletSnapshotBalanceVisible] = useState(true);
  const [emailVisible, setEmailVisible] = useState(false);

  const email = userDetails?.emailId || "";
  const maskedEmail = maskEmail(email);
  const uid = userDetails?.uuid || "--";
  const phoneNumber = userDetails?.mobileNumber || "--";
  const countryCode = userDetails?.country_code || "";
  const phoneText = maskPhone(phoneNumber, countryCode);
  const kycTier = coerceKycVerified(userDetails?.kycVerified);
  const kycLabel = kycBadgeLabel(kycTier);
  const kycTone = kycBadgeTone(kycTier);
  const isKycVerified = isKycVerifiedTier(kycTier);
  const hasDeposited = Number(snapshotUsdt) > 0;

  /** Step 3: completed once the user has at least one trade in Spot, Futures, or Margin (isolated / cross). */
  const [hasTraded, setHasTraded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const markIfAny = (items) => {
      if (!cancelled && items.length > 0) setHasTraded(true);
    };
    const marginItems = (res) => (res?.success && Array.isArray(res.data) ? res.data : []);

    AuthService.spotMeTrades({ page: 1, page_size: 1 })
      .then((res) => markIfAny(spotMeActivityItemsFromResponse(res)))
      .catch(() => { });
    AuthService.futuresExecutions({ skip: 0, limit: 1 })
      .then((res) => markIfAny(extractFuturesTrades(res)))
      .catch(() => { });
    AuthService.marginTrades({ page: 1, limit: 1 })
      .then((res) => markIfAny(marginItems(res)))
      .catch(() => { });
    AuthService.crossTrades({ page: 1, limit: 1 })
      .then((res) => markIfAny(marginItems(res)))
      .catch(() => { });

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleWalletSnapshotBalanceVisibility = () => {
    setWalletSnapshotBalanceVisible((prev) => !prev);
  };

  const stepsScrollRef = useRef(null);
  const [activeStepIdx, setActiveStepIdx] = useState(0);
  const [profileIdPanelOpen, setProfileIdPanelOpen] = useState(true);
  const stepsCount = 3;

  useEffect(() => {
    const el = stepsScrollRef.current;
    if (!el) return;

    let raf = 0;
    const recompute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const children = Array.from(el.children).filter((n) =>
          n?.classList?.contains("steps_block_dash")
        );
        if (!children.length) return;
        const cur = el.scrollLeft;
        let bestIdx = 0;
        let bestDist = Infinity;
        children.forEach((child, idx) => {
          const dist = Math.abs(child.offsetLeft - cur);
          if (dist < bestDist) {
            bestDist = dist;
            bestIdx = idx;
          }
        });
        setActiveStepIdx((prev) => (prev === bestIdx ? prev : bestIdx));
      });
    };

    recompute();
    el.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, []);

  const scrollToStep = (idx) => {
    const el = stepsScrollRef.current;
    if (!el) return;
    const children = Array.from(el.children).filter((n) =>
      n?.classList?.contains("steps_block_dash")
    );
    const target = children[idx];
    if (!target) return;
    el.scrollTo({ left: target.offsetLeft, top: 0, behavior: "smooth" });
    setActiveStepIdx(idx);
  };
  const handleCopyUid = async () => {
    if (!uid || uid === "--") return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(uid);
        alertSuccessMessage("UID copied to clipboard");
        return;
      }
    } catch {
      // Fallback below if clipboard API is unavailable or blocked.
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = uid;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch {
      // Silent fail to avoid UI interruption.
    }
  };

  return (
    <>
      <div className="top_header_dash">
        <div className="d-flex user_header_cnt_top justify-content-between align-items-center">
          <div className="user_profile">
            <button
              type="button"
              className={`arrow_toggle${profileIdPanelOpen ? " active" : ""}`}
              onClick={() => setProfileIdPanelOpen((v) => !v)}
              aria-expanded={profileIdPanelOpen}
              aria-controls="dashboard-profile-id-panel"
              aria-label={profileIdPanelOpen ? "Hide profile details" : "Show profile details"}
            >
              <i className="ri-arrow-down-s-line" aria-hidden />
            </button>
            <div className="user_left_img">
              <div className="user_img">
                <img
                  src={
                    userDetails?.profilepicture
                      ? (resolveUserAvatarUrl(userDetails.profilepicture) || "/images/user.png")
                      : "/images/user.png"
                  }
                  alt="User profile"
                  style={{ borderRadius: "50%" }}
                  onError={(e) => {
                    e.target.src = "/images/user.png";
                  }}
                />
              </div>
            </div>

            <div className="user_profile_cnt">
              <h3>
                {emailVisible
                  ? (userDetails?.emailId || userDetails?.mobileNumber || "--")
                  : (userDetails?.emailId ? maskedEmail : userDetails?.mobileNumber ? phoneText : "--")
                }
                <span className="hide">
                  <button
                    type="button"
                    onClick={() => setEmailVisible((v) => !v)}
                  >
                    <i className={emailVisible ? "ri-eye-line" : "ri-eye-off-line"} />
                    {" "}
                  </button>
                </span>
              </h3>
              <div className="d-flex gap-2 align-items-center sublevels">
                <span
                  className="vip_level"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate("/vip-services")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate("/vip-services");
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-star-fill" /> {userDetails?.vipLevel || userDetails?.vip_level || "VIP0"}
                </span>
                <span
                  className={`subdel kyc-chip kyc-chip--${kycTone}`}
                  role="button"
                  tabIndex={0}
                  onClick={openKycVerification}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openKycVerification();
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {kycLabel}
                </span>
                {/* <span className="low_level">
                  <i className="ri-circle-fill" />
                  Low
                </span> */}
              </div>
            </div>
          </div>
          <div
            id="dashboard-profile-id-panel"
            className={`profile_id_s${profileIdPanelOpen ? " active" : ""}`}
          >
            <div className="profile_id">
              <span>UID :</span>
              <div className="uid d-flex align-items-center">
                {uid}
                <button
                  type="button"
                  onClick={handleCopyUid}
                  className="bg-transparent border-0 p-0 m-1"
                  aria-label="Copy UID"
                  title="Copy UID"
                >
                  <img
                    src="/images/uid_icon.svg"
                    alt="Copy UID"
                    style={{ cursor: "pointer" }}
                  />
                </button>
              </div>
            </div>
            <div
              className="profile_id"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/fees")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/fees");
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <span>Fees</span>
              <div className="uid d-flex align-items-center">
                {" "}
                Detail
                <i className="ri-arrow-right-s-line" />
              </div>
            </div>
            <div className="profile_id kycstatus">
              <span>Phone Number</span>
              {phoneText}
              <Link className="text" to="/user_profile/kyc" />
            </div>
            {/* <div className="profile_id">
              <span>Email (Username)</span>
              <span className="textprofile">{maskedEmail}</span>
            </div> */}
            <div
              className="profile_id"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/user_profile/security/thirdPartyAccess")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/user_profile/security/thirdPartyAccess");
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <span>Third Party  Login</span>
              <span className="textprofile">
                Manage <i className="ri-arrow-right-s-line" />
              </span>
            </div>
          </div>
        </div>

        <div className="wallet_snapshot_bl">
          <div className="wallet_snapshot_bl_left">
            <span>WALLET SNAPSHOT</span>
            <p className="copycode">
              {walletSnapshotBalanceVisible ? (
                <>
                  <span className="dash-wallet-snapshot-main">
                    {formatEstimateSegment(snapshotPreferred)} {snapshotPreferredCurrency}
                  </span>
                  <span className="dash-wallet-snapshot-approx">
                    ≈ {formatEstimateSegment(snapshotUsdt)} {snapshotApproxCurrency}
                  </span>
                </>
              ) : (
                <span className="dash-wallet-snapshot-masked">****</span>
              )}
              <button
                type="button"
                onClick={toggleWalletSnapshotBalanceVisibility}
                aria-label={
                  walletSnapshotBalanceVisible ? "Hide wallet snapshot balance" : "Show wallet snapshot balance"
                }
                aria-pressed={!walletSnapshotBalanceVisible}
                title={walletSnapshotBalanceVisible ? "Hide balance" : "Show balance"}
                style={{
                  verticalAlign: "middle",
                  marginBottom: "12px",
                  appearance: "none",
                  border: "none",
                  padding: 0,
                  background: "transparent",
                  cursor: "pointer",
                  color: "inherit",
                }}
              >
                <i className={walletSnapshotBalanceVisible ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden />
              </button>
            </p>
          </div>

          <div className="wallet_snapshot_bl_right d-flex align-items-center">
            <button type="button" onClick={() => navigate("/asset_managemnet/deposit")}>
              <i className="ri-arrow-right-down-line" /> Deposit
            </button>
            <button type="button" className="withdraw_btn" onClick={() => navigate("/asset_managemnet/withdraw")}>
              <i className="ri-arrow-right-up-line" />
              Withdraw
            </button>
          </div>
        </div>
      </div>

      <div className="new_steps_crypto">
        <h3>Newcomer Perks</h3>
        <div className="steps_block_dash_outer" ref={stepsScrollRef}>
          {/* Step 1 – KYC Verification */}
          <div className={`steps_block_dash${kycTier === KYC.VERIFIED ? " completed" : kycTier === KYC.IN_PROGRESS || kycTier === KYC.UNDER_REVIEW ? " pending" : " active"}`}>
            <div className="steps_cnt">
              <span className="step_number">Step 1</span>
              <h5>
                Verify identity and
                claim <span>exciting rewards</span> in your account
              </h5>
              {kycTier === KYC.VERIFIED ? (
                <span className="verify_btn completed_btn">
                  <i className="ri-checkbox-circle-fill" /> Verified
                </span>
              ) : kycTier === KYC.UNDER_REVIEW ? (
                <span className="verify_btn pending_btn">
                  <i className="ri-time-line" /> Under review
                </span>
              ) : kycTier === KYC.IN_PROGRESS ? (
                <button
                  type="button"
                  className="verify_btn pending_btn"
                  onClick={openKycVerification}
                >
                  Finish verification
                </button>
              ) : (
                <button
                  type="button"
                  className="verify_btn"
                  onClick={openKycVerification}
                >
                  {kycTier === KYC.REJECTED
                    ? "Try again"
                    : kycTier === KYC.REKYC_REQUIRED
                      ? "Submit KYC again"
                      : "Verify Now"}
                </button>
              )}
            </div>
            <div className="steps_block_dash_img">
              <img className="dark_img" src="/images/steps_vector.svg" alt="Newcomer Perks" />
              <img className="light_img" src="/images/steps_vector_light.svg" alt="Newcomer Perks" />
            </div>
          </div>

          {/* Step 2 – First Deposit (locked until KYC verified) */}
          <div className={`steps_block_dash${!isKycVerified ? " disabled" : hasDeposited ? " completed" : " active"}`}>
            <div className="steps_cnt">
              <span className="step_number">Step 2</span>
              <h5>First Deposit</h5>
              <p>
                First deposit and boost your
                trading journey.
              </p>
              {hasDeposited ? (
                <span className="verify_btn completed_btn">
                  <i className="ri-checkbox-circle-fill" /> Deposited
                </span>
              ) : (
                <button
                  type="button"
                  className="verify_btn"
                  disabled={!isKycVerified}
                  onClick={() => navigate("/asset_managemnet/deposit")}
                >
                  Deposit Now
                </button>
              )}
            </div>
            <div className="steps_block_dash_img">
              <img className="dark_img" src="/images/steps_vector2.svg" alt="First Deposit" />
              <img className="light_img" src="/images/steps_vector_light2.svg" alt="First Deposit" />
            </div>
          </div>

          {/* Step 3 – Start Trading (locked until deposit done) */}
          <div className={`steps_block_dash${hasTraded ? " completed" : (!isKycVerified || !hasDeposited) ? " disabled" : " active"}`}>
            <div className="steps_cnt">
              <span className="step_number">Step 3</span>
              <h5>Start Trading</h5>
              <p>Make your first trade and advanced trading features.</p>
              {hasTraded ? (
                <span className="verify_btn completed_btn">
                  <i className="ri-checkbox-circle-fill" /> Traded
                </span>
              ) : (
                <button
                  type="button"
                  className="verify_btn"
                  disabled={!isKycVerified || !hasDeposited}
                  onClick={() => navigate("/trade/btc-usdt")}
                >
                  Trade Now
                </button>
              )}
            </div>
            <div className="steps_block_dash_img">
              <img className="dark_img" src="/images/steps_vector3.svg" alt="Start Trading" />
              <img className="light_img" src="/images/steps_vector_light3.svg" alt="Start Trading" />
            </div>
          </div>
        </div>

        <div className="steps_block_dash_dots" role="tablist" aria-label="Newcomer perks steps">
          {Array.from({ length: stepsCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`steps_block_dash_dot${activeStepIdx === i ? " is-active" : ""}`}
              onClick={() => scrollToStep(i)}
              aria-label={`Go to step ${i + 1}`}
              aria-current={activeStepIdx === i}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default DashboardTopContent;
